// ./src/components/generic/IconSelector/IconSelector.tsx
//
// Generic icon picker. A form-control trigger shows the selected icon; clicking
// opens a searchable, grouped modal of common event/venue/catering/music/games
// icons (see eventIcons.ts). Returns the chosen icon NAME (AppIcon registry) or
// null. Modal-based (not a popover) so it works cleanly inside drawers/sheets
// without fighting their focus trap.

import React, { useMemo, useState } from "react";
import { TextInput, ScrollArea, SimpleGrid, UnstyledButton, Tooltip, Text, Stack, Box, Button, Input, ActionIcon, Group } from "@mantine/core";
import { StackedModal } from "@em/ui";
import { useTranslation } from "react-i18next";

import { AppIcon } from "../AppIcon";
import { EVENT_ICON_GROUPS } from "./eventIcons";

export interface IconSelectorProps {
  /** Selected icon name, or null. */
  value: string | null;
  /** Called with the chosen icon name, or null when cleared. */
  onChange: (icon: string | null) => void;
  label?: string;
  description?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
}

const IconCell: React.FC<{ name: string; selected: boolean; onSelect: (name: string) => void }> = ({
  name,
  selected,
  onSelect,
}) => (
  <Tooltip label={name} withArrow openDelay={300}>
    <UnstyledButton
      onClick={() => onSelect(name)}
      aria-label={name}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        aspectRatio: "1",
        borderRadius: "var(--mantine-radius-md)",
        border: `1px solid ${selected ? "var(--mantine-color-blue-filled)" : "var(--mantine-color-default-border)"}`,
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : "transparent",
        color: selected ? "var(--mantine-color-blue-filled)" : undefined,
      }}
    >
      <AppIcon iconName={name} size={22} />
    </UnstyledButton>
  </Tooltip>
);

export const IconSelector: React.FC<IconSelectorProps> = ({
  value,
  onChange,
  label,
  description,
  placeholder,
  error,
  disabled = false,
  size = "md",
}) => {
  const { t } = useTranslation(["common"]);

  const [opened, setOpened] = useState(false);
  const [search, setSearch] = useState("");

  const query = search.toLowerCase().trim();

  // While searching, collapse to a single flat filtered grid; otherwise show the
  // curated groups.
  const filteredGroups = useMemo(() => {
    if (!query) {
      return EVENT_ICON_GROUPS;
    }

    const matches = Array.from(
      new Set(
        EVENT_ICON_GROUPS.flatMap((g) => g.icons).filter((name) => name.toLowerCase().includes(query))
      )
    );

    return [{ key: "results", label: t("common:searchResults", { defaultValue: "Results" }), icons: matches }];
  }, [query, t]);

  const open = () => {
    if (!disabled) {
      setSearch("");
      setOpened(true);
    }
  };

  const handleSelect = (name: string) => {
    onChange(name);
    setOpened(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const resolvedPlaceholder =
    placeholder ?? t("common:iconSelector.placeholder", { defaultValue: "Choose an icon" });

  return (
    <Input.Wrapper label={label} description={description} error={error} size={size}>
      <Button
        variant="default"
        fullWidth
        justify="space-between"
        onClick={open}
        disabled={disabled}
        mt={label ? 4 : undefined}
        size={size}
        leftSection={
          value ? <AppIcon iconName={value} size={18} /> : <AppIcon iconName="MoodSmile" size={18} />
        }
        rightSection={
          value ? (
            <ActionIcon
              component="div"
              variant="subtle"
              color="gray"
              size="md"
              onClick={handleClear}
              aria-label={t("common:buttons.clear", { defaultValue: "Clear" })}
            >
              <AppIcon iconName="X" size={14} />
            </ActionIcon>
          ) : (
            <AppIcon iconName="ChevronDown" size={16} />
          )
        }
        styles={{ label: { flex: 1, textAlign: "left", fontWeight: 400, color: value ? undefined : "var(--mantine-color-dimmed)" } }}
      >
        {value ?? resolvedPlaceholder}
      </Button>

      <StackedModal
        opened={opened}
        onClose={() => setOpened(false)}
        title={t("common:iconSelector.title", { defaultValue: "Choose an icon" })}
        size="lg"
        centered
      >
        <Stack gap="md">
          <TextInput
            placeholder={t("common:iconSelector.search", { defaultValue: "Search icons…" })}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<AppIcon iconName="Search" size={16} />}
            data-autofocus
          />

          <ScrollArea.Autosize mah={420} type="auto">
            <Stack gap="lg">
              {filteredGroups.map((group) =>
                group.icons.length === 0 ? null : (
                  <Box key={group.key}>
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="xs">
                      {t(`common:iconSelector.groups.${group.key}`, { defaultValue: group.label })}
                    </Text>
                    <SimpleGrid cols={{ base: 5, sm: 8 }} spacing="xs">
                      {group.icons.map((name) => (
                        <IconCell
                          key={name}
                          name={name}
                          selected={value === name}
                          onSelect={handleSelect}
                        />
                      ))}
                    </SimpleGrid>
                  </Box>
                )
              )}

              {filteredGroups.every((g) => g.icons.length === 0) && (
                <Text size="sm" c="dimmed" ta="center" py="lg">
                  {t("common:iconSelector.noResults", { defaultValue: "No icons match your search." })}
                </Text>
              )}
            </Stack>
          </ScrollArea.Autosize>

          {value && (
            <Group justify="flex-end">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<AppIcon iconName="X" size={16} />}
                onClick={() => {
                  onChange(null);
                  setOpened(false);
                }}
              >
                {t("common:iconSelector.remove", { defaultValue: "Remove icon" })}
              </Button>
            </Group>
          )}
        </Stack>
      </StackedModal>
    </Input.Wrapper>
  );
};
